'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import NavBar from '../../home-page/components/NavBar';
import FooterSection from '../../home-page/components/FooterSection';
import { servicesService } from '@/lib/services/servicesService';
import { generateLocalBusinessSchema, generateBreadcrumbSchema } from '@/lib/utils/metadata';
import { trackEvent } from '@/lib/analytics';

const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || '';


interface ServicePageProps {
  params: {
    slug: string;
  };
}

interface ServiceData {
  title: string;
  description: string;
  features: string[];
  pricing?: string;
  benefits: string[];
  callToAction: {
    title: string;
    description: string;
    primaryButton: { text: string; link: string };
    secondaryButton: { text: string; link: string };
  };
  featuredImageUrl?: string;
}

export default function ServicePage({ params }: ServicePageProps) {
  const [service, setService] = useState<ServiceData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadService = async () => {
      try {
        const data = await servicesService.getBySlug(params.slug);
        if (data) {
          setService({
            title: data.title,
            description: data.description,
            features: data.features,
            pricing: data.pricing,
            benefits: data.benefits,
            callToAction: data.callToAction,
            featuredImageUrl: data.featuredImageUrl,
          });
        }
      } catch (error: any) {
        console.error('Failed to load service:', error.message);
      } finally {
        setLoading(false);
      }
    };

    loadService();
  }, [params.slug]);

  const breadcrumbSchema = service ? generateBreadcrumbSchema([
    { name: 'Home', url: `${SITE_URL}/home-page` },
    { name: 'Services', url: `${SITE_URL}/home-page#services` },
    { name: service.title, url: `${SITE_URL}/services/${params.slug}` },
  ]) : null;

  const serviceSchema = service ? {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    description: service.description,
    provider: {
      '@type': 'Organization',
      name: 'GoGedit',
      url: SITE_URL,
    },
    areaServed: {
      '@type': 'State',
      name: 'Ontario',
    },
    serviceType: 'Moving and Delivery',
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      priceCurrency: 'CAD',
      priceSpecification: service.pricing ? {
        '@type': 'PriceSpecification',
        price: service.pricing,
        priceCurrency: 'CAD',
      } : undefined,
    },
  } : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="pt-24 pb-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-brand-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-body-text">Loading...</p>
          </div>
        </div>
        <FooterSection />
      </div>
    );
  }

  if (!service) {
    return (
      <div className="min-h-screen bg-white">
        <NavBar />
        <div className="pt-24 pb-16 text-center">
          <h1 className="text-4xl font-bold text-navy-dark">Service Not Found</h1>
          <Link href="/home-page" className="text-brand-purple hover:underline mt-4 inline-block">
            Return to Home
          </Link>
        </div>
        <FooterSection />
      </div>
    );
  }

  const localBusinessSchema = generateLocalBusinessSchema({
    name: service.title,
    description: service.description,
    url: `${SITE_URL}/services/${params.slug}`,
  });

  return (
    <>
      {breadcrumbSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
      )}
      {serviceSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }}
        />
      )}
      <div className="min-h-screen bg-white">
        <NavBar />

        <div className="pt-24 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Hero Section */}
            <div className="text-center mb-16">
              <h1 className="text-5xl font-bold text-navy-dark mb-4">{service.title}</h1>
              <p className="text-xl text-body-text max-w-3xl mx-auto">{service.description}</p>
              {service.pricing && (
                <div className="mt-6">
                  <span className="inline-block bg-brand-green text-white px-6 py-3 rounded-full text-lg font-semibold">
                    {service.pricing}
                  </span>
                </div>
              )}
            </div>

            {/* Features and Benefits Grid */}
            <div className="grid md:grid-cols-2 gap-12 mb-16">
              {/* Features */}
              <div className="bg-purple-light rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-navy-dark mb-6">Features</h2>
                <ul className="space-y-3">
                  {service?.features?.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <svg
                        className="w-6 h-6 text-brand-purple mr-3 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-body-text">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Benefits */}
              <div className="bg-green-50 rounded-2xl p-8">
                <h2 className="text-3xl font-bold text-navy-dark mb-6">Benefits</h2>
                <ul className="space-y-3">
                  {service?.benefits?.map((benefit, index) => (
                    <li key={index} className="flex items-start">
                      <svg
                        className="w-6 h-6 text-brand-green mr-3 flex-shrink-0 mt-0.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-body-text">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Call to Action */}
            {service.callToAction?.title && (
              <div className="bg-gradient-to-r from-brand-purple to-brand-green rounded-2xl p-12 text-center text-white mb-16">
                <h2 className="text-3xl font-bold mb-4">{service.callToAction.title}</h2>
                <p className="text-xl mb-8 opacity-90">{service.callToAction.description}</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  {service.callToAction.primaryButton?.text && (
                    <Link
                      href={service.callToAction.primaryButton.link}
                      onClick={() => trackEvent('service_cta_click', { service: service.title, button: 'primary' })}
                      className="bg-white text-brand-purple px-8 py-4 rounded-full text-lg font-medium hover:bg-opacity-90 transition-all"
                    >
                      {service.callToAction.primaryButton.text}
                    </Link>
                  )}
                  {service.callToAction.secondaryButton?.text && (
                    <Link
                      href={service.callToAction.secondaryButton.link}
                      onClick={() => trackEvent('service_cta_click', { service: service.title, button: 'secondary' })}
                      className="bg-brand-green text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-opacity-90 transition-all border-2 border-white"
                    >
                      {service.callToAction.secondaryButton.text}
                    </Link>
                  )}
                </div>
              </div>
            )}

            {/* Add internal link before How It Works section */}
            <div className="bg-purple-light rounded-2xl p-8 text-center mb-16">
              <h2 className="text-2xl font-bold text-navy-dark mb-4">Ready to Get Started?</h2>
              <p className="text-body-text mb-6">
                Sign up as a user and start enjoying hassle-free delivery services today.
              </p>
              <Link
                href="/be-a-user"
                className="inline-block bg-brand-purple text-white px-8 py-4 rounded-full text-lg font-medium hover:bg-opacity-90 transition-all"
              >
                Sign Up as a User
              </Link>
            </div>

            {/* How It Works */}
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-brand-purple rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-navy-dark mb-2">1. Request Service</h3>
                <p className="text-body-text">Submit your service request with details</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-brand-green rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-navy-dark mb-2">2. Get Matched</h3>
                <p className="text-body-text">We connect you with the right driver</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-brand-purple rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-navy-dark mb-2">3. Track & Complete</h3>
                <p className="text-body-text">Track in real-time and confirm delivery</p>
              </div>
            </div>
          </div>
        </div>

        <FooterSection />
      </div>
    </>
  );
}